import { JService } from "../components/api-service/ApiService";
import { setDialer } from "../components/features/dialer/dialer";
import { store } from "../index";
import libphonenumber from "google-libphonenumber";

const suspendedStatuses = [
  "TRIAL_EXPIRED",
  "SUSPENDED_UCC",
  "SUSPENDED_DOCS",
  "PAUSED",
  "INACTIVE",
];

export const AUTO_CANCELLED = "AUTO_CANCELLED";

export const formatFileSize = (number) => {
  if (number < 1024) {
    return `${number} bytes`;
  } else if (number >= 1024 && number < 1048576) {
    return `${(number / 1024).toFixed(1)} KB`;
  } else if (number >= 1048576) {
    return `${(number / 1048576).toFixed(1)} MB`;
  }
};

export const changeDateFormat = (d) => {
  if (!d) return "";
  const parts = d.split("-");

  d = `${parts[2]}-${parts[1]}-${parts[0]}`;
  return d;
};

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
export const formatDate = (d) => {
  if (!d) return "";

  if (["-", "+"].includes(String(d)?.at(-5))) {
    d = d?.slice(0, -5) || d;
  }

  const dateObj = new Date(d);

  if (isNaN(dateObj.getTime())) {
    return "Invalid Date";
  }

  const day = dateObj.getDate();
  const month = dateObj.getMonth();
  const year = dateObj.getFullYear();

  let ordinalSuffix = "";

  switch (day % 10) {
    case 1:
      ordinalSuffix = "st";
      break;
    case 2:
      ordinalSuffix = "nd";
      break;
    case 3:
      ordinalSuffix = "rd";
      break;
    default:
      ordinalSuffix = "th";
  }
  if (day > 3 && day < 21) ordinalSuffix = "th";

  const dateStamp = day + ordinalSuffix + " " + monthNames[month] + " " + year;

  return dateStamp;
};

export const formatTime = (d) => {
  if (["-", "+"].includes(String(d)?.at(-5))) {
    d = d?.slice(0, -5) || d;
  }
  let _d = "";
  let _hour = new Date(d).getHours();
  let _minute = new Date(d).getMinutes();
  if (_minute < 10) _minute = "0" + _minute;
  if (_hour > 12) {
    // 13:00 hours etc
    _hour -= 12;
    // no zero needed at the start
    if (_hour >= 10) _d = _hour + ":" + _minute + " PM";
    else _d = "0" + _hour + ":" + _minute + " PM";
  } else if (_hour >= 10 && _hour <= 12) {
    // 10:00 to 12:59 hours
    if (_hour === 12) _d = _hour + ":" + _minute + " PM";
    else _d = _hour + ":" + _minute + " AM";
  } else if (_hour < 10) {
    _d = "0" + _hour + ":" + _minute + " AM";
  }
  return _d;
};

export const capitalizeString = (str) => {
  return str[0].toUpperCase() + str.slice(1).toLowerCase();
};

// Function will accept time in minutes and return an object with hrs,mins,secs
// Eg: 3242.3214 -> {hrs:52, mins:2, secs:19}
// Eg: 68.489 -> {hrs:1, mins:8, secs:29}
export const getHMSfromMins = (time) => {
  const hrs = Math.floor(time / 60);
  time = time % 60;
  const mins = Math.floor(time);
  time = time - mins;
  const secs = Math.floor(time * 60);

  return { hrs: hrs, mins: mins, secs: secs };
};

// Function will accept time in miliseconds and return an object with hrs,mins,secs
export const getHMSfromMilliSecs = (time) => {
  const secs = parseInt(time / 1000) % 60;
  const mins = parseInt((time / (1000 * 60)) % 60);
  const hrs = parseInt((time / (1000 * 60 * 60)) % 24);

  return { hrs: hrs, mins: mins, secs: secs };
};

export const validateEmail = (email) => {
  const reg = /^[\w._-]+[+]?[\w._-]+@[\w.-]+\.[a-zA-Z]{2,15}$/;
  return email && email?.trim()?.length > 0 && reg.test(email);
};

export const validatePhoneNumber = (cc, phoneNumber) => {
  const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
  try {
    const parsedNumber = phoneUtil.parseAndKeepRawInput(
      cc ? cc + phoneNumber : "" + phoneNumber,
    );

    return phoneUtil.isValidNumber(parsedNumber);
  } catch (error) {
    console.log("Error while validating phonenumber as", error?.message);
  }
};

export const validateURL = (value) => {
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > 1000) return false;
  const regex = new RegExp(
    "^(https?:\\/\\/)" +
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" +
      "localhost|" +
      "\\d{1,3}(\\.\\d{1,3}){3})" +
      "(:\\d+)?" +
      "(\\/[-a-z\\d%@_.~+&:]*)*" +
      "(\\?[$;&a-z\\d%@_.,~+&:=-]*)?" +
      "(\\#[-a-z\\d_]*)?$",
    "i",
  );
  return regex.test(trimmed);
};

export const parseNumber = (phoneNumber) => {
  const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
  let cc = "";
  let number = "";
  try {
    const numberObj = phoneUtil.parseAndKeepRawInput(phoneNumber);
    cc = numberObj.getCountryCodeOrDefault();
    if (cc) cc = `+${cc}`;
    number = phoneUtil.getNationalSignificantNumber(numberObj);
  } catch (err) {
    console.log("Error while parsing phone number", phoneNumber, err);
  } finally {
    return { cc, number };
  }
};

// sleep for ms milliseconds
export async function sleep(ms) {
  return new Promise((resolve, reject) => {
    if (!Number.isInteger(ms) || ms < 0) {
      // ms is not an integer
      reject("expected 'positive integer' for argument 'ms', got ", ms);
    }
    setTimeout(() => {
      resolve(`slept for "${ms / 1000}" seconds`);
    }, ms);
  });
}

export const formatCurrentDate = (date = Date.now()) => {
  return new Date(date).toLocaleDateString("en-IN", {
    day: "numeric",
    weekday: "long",
    month: "long",
    year: "numeric",
  });
};

export const fetchData = (url, setterFn, resourceName) => {
  JService.get(url)
    .then((res) => {
      if (!res?.success)
        throw new Error(res?.message || `Could not retrieve ${resourceName}`);
      setterFn(res?.data);
      return res?.data;
    })
    .catch((err) => console.error(`Could not retrieve ${resourceName}`, err));
};

export const initiateCall = (e, candidateNumber, contact_list_id) => {
  e.stopPropagation();
  const maskNumbers = store.getState()?.auth?.maskNumbers;
  try {
    let _phone = candidateNumber;
    store.dispatch(
      setDialer({
        phone_number: _phone,
        country_code: null,
        isVisible: true,
        contact_list_id,
        maskNumbers,
      }),
    );
  } catch (err) {
    console.error("Error while dispatching setDialer in initiateCall", err);
  }
};

export function filtersReducer(state, action) {
  const { payload, payloadFor, extraPayload } = action?.payload || {};
  switch (action.type) {
    case "setPayloadToAllOfType": {
      return {
        ...state,
        [payloadFor]: {
          ...state[payloadFor],
          all: payload,
          ...(payloadFor === "teams" ? { teamUserMapping: extraPayload } : {}),
        },
      };
    }
    case "handleSelectedOfType": {
      return {
        ...state,
        [payloadFor]: { ...state[payloadFor], selected: payload },
      };
    }
    case "handleClearOfType": {
      return {
        ...state,
        [payloadFor]: { ...state[payloadFor], selected: [] },
      };
    }
    case "clearAllFilters": {
      const temp = { ...state };
      Object.keys(state)?.forEach((key) => {
        if (typeof temp[key] === "object" && !Array.isArray(temp[key])) {
          temp[key] = {
            ...temp[key],
            ...("selected" in temp[key] ? { selected: [] } : {}),
          };
        }
      });
      if ("searchText" in temp) temp["searchText"] = "";
      return temp;
    }
    case "setSearchText": {
      if (typeof action.payload !== "string") return state;
      return {
        ...state,
        searchText: action.payload,
      };
    }
    case "overwriteState": {
      let __payload = {
        ...JSON.parse(localStorage.getItem("frejun-temp-logs-filters")),
        persist: false,
      };
      return __payload;
    }
    default:
      return state;
  }
}

export const getOrgURLParam = () => {
  const reduxOrg = store.getState().account.value;
  let orgUrlParam = "";
  if (
    reduxOrg?.data?.profile?.account_holder &&
    reduxOrg?.allOrgs?.length > 1
  ) {
    orgUrlParam = reduxOrg?.selectedOrg + "/";
    if (!reduxOrg?.selectedOrg && reduxOrg?.selectedOrg !== 0) orgUrlParam = "";
  }
  return orgUrlParam;
};

export const getPermissionId = (category = "", action = "") => {
  let permissions = store?.getState()?.auth?.permissionsList;
  if (!Array.isArray(permissions)) {
    if (typeof permissions === 'string') {
      permissions = JSON.parse(permissions);
    } 
    else {
      return undefined;
    };
  }
  return permissions?.find((elem) => {
    return (
      (category ? category == elem.category : true) &&
      (action ? action == elem.action : true)
    );
  })?.id;
};

export const redirectOnAuth = () => {
  if (window.location.pathname.startsWith("/oauth")) return;

  const paths = store.getState()?.auth?.validPaths;
  const validTab = Object.keys(paths || {}).find(
    (key) => paths[key].allowed && "path" in paths[key],
  );
  const path = paths[validTab].path;

  var redirect = localStorage.getItem("frejun-login-redirect");
  if (redirect) redirect = JSON.parse(redirect);

  const searchParams = new URLSearchParams(window.location.search);
  const supportParam = searchParams.get("support") ? "/?support=true" : "";

  // login successful
  if (!redirect || !Object.keys(redirect)?.length || redirect?.isRead) {
    return `${path}/${supportParam}`;
  }
  // redirect url not read; redirect to url and update storage
  else if (!redirect?.isRead) {
    localStorage.setItem(
      "frejun-login-redirect",
      JSON.stringify({ url: redirect?.url, isRead: true }),
    );
    setTimeout(() => (window.location.href = redirect?.url), 50);
  }
};

export function to24HourFormat(isoString) {
  if (!isoString) return;
  const cleanIso = isoString?.replace(/\.\d+$/, "");
  const date = new Date(cleanIso);
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

export function isSameLocalDate(aIso, bIso) {
  const d1 = new Date(aIso);
  const d2 = new Date(bIso);

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

export function isWithin24Hours(isoString) {
  const cleaned = isoString.replace(/(\.\d{3})\d+/, "$1");
  const then = new Date(cleaned);
  const now = Date.now();
  const diffMs = Math.abs(now - then.getTime());
  return diffMs <= 24 * 60 * 60 * 1000;
}
export const isTrialSubscription = (status) => {
  return ["TRIAL", "TRIAL_EXPIRED", "PRE_ACTIVATION"].includes(status);
};

export const isSuspended = (status) => suspendedStatuses.includes(status);

export const getPasswordStrength = (password) => {
  const criteria = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    digit: /\d/.test(password),
    specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const fulfilledCriteria = Object.values(criteria).filter(
    (criterion) => criterion,
  ).length;

  let score = 0;
  if (fulfilledCriteria === 5) {
    score = "Strong";
  } else if (fulfilledCriteria >= 3) {
    score = "Medium";
  } else if (fulfilledCriteria >= 1) {
    score = "Weak";
  }

  return score;
};

export const strengthColors = {
  Weak: "indianred",
  Medium: "orange",
  Strong: "#2eb258",
};

export function getOtherParamsObj(otherparamsString) {
  let extraParamsObj = Object.fromEntries(
    otherparamsString.split("&").map((part) => {
      let [key, value] = part.split("=");
      return [key, value];
    }),
  );

  return extraParamsObj;
}

export const sentenceCase = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const calcPrice = (plan) => {
  let price = Number(plan?.price);
  let discountedPrice = Number(plan?.discounted_price);
  const billingCycle = plan?.billing_cycle;
  if (billingCycle === "MONTHLY") return [price, discountedPrice.toFixed(2)];
  if (billingCycle === "QUARTERLY") {
    price /= 3;
    discountedPrice /= 3;
  } else if (billingCycle === "BIANNUAL") {
    price /= 6;
    discountedPrice /= 6;
  } else if (billingCycle === "ANNUAL") {
    price /= 12;
    discountedPrice /= 12;
  }

  return [price, discountedPrice.toFixed(2)];
};

export const getBasePrice = (discountedPrice, billingCycle) => {
  let basePrice = discountedPrice;
  if (billingCycle === "QUARTERLY") {
    basePrice *= 3;
  } else if (billingCycle === "BIANNUAL") {
    basePrice *= 6;
  } else if (billingCycle === "ANNUAL") {
    basePrice *= 12;
  }
  return basePrice;
};

export const proratedCharge = (startDate, endDate, basePrice) => {
  const oneDay = 86400000;
  const billingPeriod = (new Date(endDate) - new Date(startDate)) / oneDay;
  if (billingPeriod === 0) return { amount: (0).toFixed(2), isProrated: false };
  const proratedTerm =
    (new Date(endDate).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) /
    oneDay;
  const amount = (proratedTerm / billingPeriod) * basePrice;
  const isProrated = amount < basePrice;
  return { amount: amount.toFixed(2), isProrated };
};

export const inrFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
});

export const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const currencyFormatter = (currency, value) => {
  if (currency === "₹") return inrFormatter.format(value);
  return usdFormatter.format(value);
};


export const fetchAccountProfile = async () => {
  try {
    const response = await JService.get(
      "/api/v1/auth/retrieve-account-profile/"
    );

    if (!response?.success)
      throw Error(response?.message || "Failed to fetch account details");
    return response;
  } catch (err) {
    console.error("Error while fetching account details:", err);
  }
};